// Render markdown + LaTeX (KaTeX) das respostas do tutor. Mesma receita do
// admin (testar-ia/page.tsx) + LLM-Forge: escapeBrackets converte os
// delimitadores `\( ... \)` / `\[ ... \]` que o OpenAI emite pros delimitadores
// `$ ... $` / `$$ ... $$` que o KaTeX entende.
//
// O HTML resultante passa por DOMPurify pra eliminar qualquer risco de XSS
// (o tutor é controlado mas tokens vêm do LLM — defesa em profundidade).

import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import markedKatex from 'marked-katex-extension';

// Importa o CSS do KaTeX como string e injeta no Shadow DOM via <style>
// (o Shadow DOM bloqueia stylesheets externos por padrão).
import katexCss from 'katex/dist/katex.min.css?inline';

const marked = new Marked({
  gfm: true,
  breaks: true,
});

// marked-katex-extension precisa de delimitadores $...$ (inline) e $$...$$ (display)
marked.use(
  markedKatex({
    throwOnError: false,
    nonStandard: true,
  }),
);

/**
 * Converte LaTeX delimitado por \(..\) / \[..\] (OpenAI default) pros
 * delimitadores $..$ / $$..$$ que o marked-katex-extension reconhece.
 * Ignora blocos de código (``` e `) pra não quebrar code samples.
 */
function escapeBrackets(text: string): string {
  const pattern = /(```[\s\S]*?```|`.*?`)|\\\[([\s\S]*?[^\\])\\\]|\\\((.*?)\\\)/g;
  return text.replace(pattern, (match, codeBlock, squareBracket, roundBracket) => {
    if (codeBlock) return codeBlock;
    if (squareBracket) return `$$${squareBracket}$$`;
    if (roundBracket) return `$${roundBracket}$`;
    return match;
  });
}

// Tags/atributos MathML que o KaTeX emite — precisam sobreviver ao DOMPurify.
const KATEX_SANITIZE = {
  ADD_TAGS: ['math', 'mfrac', 'mrow', 'mi', 'mn', 'mo', 'msqrt', 'msup', 'msub',
             'munderover', 'mtext', 'annotation', 'semantics', 'mstyle', 'mtable',
             'mtr', 'mtd'],
  ADD_ATTR: ['mathvariant', 'mathbackground', 'mathcolor', 'mathsize', 'displaystyle',
             'scriptlevel', 'columnalign', 'rowalign', 'columnspacing', 'rowspacing'],
};

/**
 * Markdown + LaTeX → HTML sanitizado. Output é seguro pra injetar via unsafeHTML.
 */
export function renderMarkdown(input: string): string {
  if (!input) return '';
  const pre = escapeBrackets(input);
  const html = marked.parse(pre) as string;
  return DOMPurify.sanitize(html, KATEX_SANITIZE);
}

/**
 * Igual ao renderMarkdown, mas inline: sem wrappers de bloco (`<p>`), pra usar
 * dentro de headings/spans onde `<p>` seria HTML inválido (ex.: o tema do
 * resumo, que vai num `<h3>`). Cobre LaTeX inline (`$..$` / `\(..\)`).
 */
export function renderMarkdownInline(input: string): string {
  if (!input) return '';
  const pre = escapeBrackets(input);
  const html = marked.parseInline(pre) as string;
  return DOMPurify.sanitize(html, KATEX_SANITIZE);
}

/** CSS do KaTeX pra injetar no Shadow DOM. */
export const katexStyles = katexCss;
